/* eslint-disable no-use-before-define */
/* eslint-disable no-console */

import {
  ApolloClient, InMemoryCache, ApolloLink, split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import apolloLogger from 'apollo-link-logger';
import { persistCacheSync, LocalStorageWrapper } from 'apollo3-cache-persist';
import { createUploadLink } from 'apollo-upload-client';
import MutationQueueLink from '@adobe/apollo-link-mutation-queue';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { createConsumer } from '@rails/actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import messageTypePolicy from './cache/messageTypePolicy';

const AUTH_HEADERS = 'uniboxi-auth';

const createActionCableLink = () => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/cable`;

  const cable = createConsumer(url);
  const cableUrl = cable.url;

  Object.defineProperty(cable, 'url', {
    get() {
      const authHeadersString = localStorage.getItem(AUTH_HEADERS);
      let newUrl = cableUrl;
      if (authHeadersString) {
        const authHeaders = JSON.parse(authHeadersString);
        newUrl = `${cableUrl}?access-token=${authHeaders['access-token']}&client=${authHeaders.client}&uid=${encodeURIComponent(authHeaders.uid)}`;
      }

      return newUrl;
    },
  });

  return new ActionCableLink({ cable, connectionParams: () => ({ cualquiera: 'hola' }) });
};

const actionCableLink = createActionCableLink();

function reconnectActionCableLink() {
  // Reabrimos el MISMO consumer en vez de reemplazar el link: las subscriptions
  // que Apollo ya tiene abiertas están atadas a este cable, así que recrearlo las
  // dejaba huérfanas (el tiempo real moría hasta refrescar). Al reabrir, el getter
  // de la URL toma el token nuevo y ActionCable re-suscribe cada canal activo en el
  // `welcome` (subscriptions.reload()).
  actionCableLink.cable.connection.reopen();
}

const middlewareLink = new ApolloLink((operation, forward) => {
  const { operationName } = operation;

  let authHeaders = {};

  if (!['userLogin',
    'logout',
    'register',
    'confirmEmail',
    'resendConfirmation',
    'createPotential',
    'updateWidgetClicks',
    'userUpdatePasswordWithToken',
  ].includes(operationName)) {
    authHeaders = JSON.parse(localStorage.getItem(AUTH_HEADERS) || '{}');
  }

  operation.setContext(({ headers }) => ({
    headers: {
      ...authHeaders,
      ...headers,
    },
  }));

  return forward(operation);
});

const afterwareLink = new ApolloLink((operation, forward) => forward(operation).map((response) => {
  const context = operation.getContext();
  const {
    response: { headers } = {},
  } = context;

  if (headers) {
    const accessToken = headers.get('access-token');

    if (accessToken) {
      const authHeaders = {
        'access-token': accessToken,
        client: headers.get('client'),
        uid: headers.get('uid'),
      };

      const previousAuthHeaders = JSON.parse(localStorage.getItem(AUTH_HEADERS) || '{}');

      if (JSON.stringify(authHeaders) !== JSON.stringify(previousAuthHeaders)) {
        console.log(JSON.stringify(authHeaders), JSON.stringify(previousAuthHeaders));
        localStorage.setItem(AUTH_HEADERS, JSON.stringify(authHeaders));
        reconnectActionCableLink();
      }
    }
  }

  return response;
}));

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({
      message, locations, path, extensions,
    }) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

      if (extensions && extensions.code === 'AUTHENTICATION_ERROR') {
        localStorage.clear();
        client.clearStore().then(() => window.location.replace('/login'));
      }
    });
  }

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const httpLink = createUploadLink({
  uri: `${process.env.REACT_APP_API_ENDPOINT || ''}/graphql`,
});

const terminatingLink = split(
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === 'OperationDefinition' && operation === 'subscription';
  },
  (operation, forward) => actionCableLink.request(operation, forward),
  httpLink,
);

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        usersMessagesData: relayStylePagination(),
        usersLeadKindsData: relayStylePagination(),
        userActiveClientsCount: relayStylePagination(),
        userResponseTimes: relayStylePagination(),
        batchSends: relayStylePagination(),
      },
    },
    Company: {
      merge: true,
      fields: {
        salesMetrics: {
          keyArgs: [
            'filters',
            'datePeriod',
            'timeZone',
            'closingSortBy',
            'closingSortDirection',
            'summarySortBy',
            'summarySortDirection',
            'includeChartBuckets',
            'includePerUserRows',
            'includeClosingSalesRows',
            'includeTotalSalesRows',
            'includeSalesSummaryPerUserRows',
          ],
        },
      },
    },
    Lead: {
      fields: {
        messages: {
          merge(existing = [], incoming, { readField }) {
            if (!incoming) return existing;
            if (!existing.length) return incoming;

            const merged = new Map();
            const addRef = (ref) => {
              const id = readField('id', ref);
              if (id) merged.set(id, ref);
            };

            existing.forEach(addRef);
            incoming.forEach(addRef);

            // Long conversations re-merge on every incoming message; parsing each
            // date once keeps the sort from re-parsing them per comparison.
            return Array.from(merged.values())
              .map(ref => ({ ref, time: new Date(readField('date', ref) || 0).getTime() }))
              .sort((a, b) => a.time - b.time)
              .map(item => item.ref);
          },
        },
      },
    },
    Message: messageTypePolicy,
  },
});

// Persisting extracts and JSON-stringifies the whole normalized cache on the main
// thread, a cost that grows with the cache. The default `write` trigger pays it on
// every cache write (each incoming message, each query response), so persistence
// only runs when the user is leaving the tab, which is all a boot-time restore needs.
const persistOnTabHidden = (persist) => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') persist();
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', persist);
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', persist);
  };
};

persistCacheSync({
  cache,
  storage: new LocalStorageWrapper(window.localStorage),
  trigger: persistOnTabHidden,
  debounce: 0,
});

// Console-logged operations are retained by the browser for the life of the tab,
// which leaks memory on tabs that stay open 24/7, so in production the logger is
// opt-in per machine: run `localStorage.setItem('apolloLogger', 'on')` and reload.
const apolloLoggerEnabled = process.env.NODE_ENV === 'development'
  || localStorage.getItem('apolloLogger') === 'on';

const client = new ApolloClient({
  link: ApolloLink.from([
    new MutationQueueLink(),
    errorLink,
    ...(apolloLoggerEnabled ? [apolloLogger] : []),
    middlewareLink,
    afterwareLink,
    terminatingLink,
  ]),
  cache,
});

// A tab that was asleep (hibernation, long stay in background) missed every
// subscription event, and ActionCable re-subscribes on wake but has no replay,
// so the mounted queries are refetched in the background to backfill the gap.
// Short tab switches stay under the threshold and refetch nothing.
const WAKE_GAP_MS = 5 * 60 * 1000;
const HEARTBEAT_MS = 30 * 1000;

const installWakeRefetch = (apolloClient) => {
  let lastBeat = Date.now();
  let hiddenAt = null;

  const refetch = () => apolloClient.reFetchObservableQueries().catch(() => {});

  // The heartbeat catches waking up with the tab already visible, where no
  // visibilitychange fires: the interval cannot tick while the machine is off,
  // so a gap far beyond the interval means the machine was asleep.
  setInterval(() => {
    const gap = Date.now() - lastBeat;
    lastBeat = Date.now();
    if (gap > WAKE_GAP_MS && document.visibilityState === 'visible') refetch();
  }, HEARTBEAT_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    const wasHiddenLong = hiddenAt && Date.now() - hiddenAt > WAKE_GAP_MS;
    hiddenAt = null;
    if (wasHiddenLong) {
      lastBeat = Date.now();
      refetch();
    }
  });
};

installWakeRefetch(client);

export default client;
